import React, { FormEvent, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Api } from '../../api';
import { User } from '../../models/User';
import './AddPersonInput.scss';

import 'react-datepicker/dist/react-datepicker.css';
import nl from 'date-fns/locale/nl';
import { useHistory } from 'react-router';
import { ERouterPath } from '../router/ERouterPath';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { IUpdatePerson } from '../../api/types';
import Accordion from 'react-bootstrap/esm/Accordion';
import { Icon } from '../icon/Icon';
import classNames from 'classnames';
import { IDossierWithOptionalRelation } from '../../models/DossierV2';
import { DossiersTable } from '../files-table/DossiersTable';
import { MultiDossierUnreadStatus } from '../../models/UnreadStatus';
registerLocale('nl', nl);

const PersonDossiers = ({ personId }: { personId: number }): JSX.Element => {
  const [dossiers, setDossiers] = useState<IDossierWithOptionalRelation[]>([]);
  const [unreadStatus, setUnreadStatus] = useState<MultiDossierUnreadStatus|null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDossiers([]);
    setIsLoading(true);
    setError('');
    setUnreadStatus(null);

    Api.listPersonDossiersWithUserRelation(personId)
      .then(list => {
        setDossiers(list);
        return Api.getUnreadStatusAllDossiers();
      })
      .then(unread => setUnreadStatus(unread))
      .catch((error) => {
        setError('Ophalen dossiers mislukt');
        console.error('Loading dossiers failed:', error);
      })
      .finally(() => setIsLoading(false));
  }, [personId]);

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (isLoading || !unreadStatus) {
    return <Spinner variant="primary" animation="border" />;
  }

  return <DossiersTable dossiersWithRelation={dossiers} badges={unreadStatus} />;
};

const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface IPersonInputProps {
  addToDossier?: number;
  updatePersonId?: number;
}

export const PersonInput = ({ addToDossier, updatePersonId }: IPersonInputProps): JSX.Element => {
  const [carewebId, setCarewebId] = useState<number|undefined>();
  const [carewebIdError, setCarewebIdError] = useState<string>('');
  const [initials, setInitials] = useState<string>('');
  const [initialsError, setInitialsError] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<Date|undefined>(undefined);
  const [dateOfBirthError, setDateOfBirthError] = useState<string>('');
  const [createAccount, setCreateAccount] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loadingPerson, setLoadingPerson] = useState<boolean>(false);
  const [person, setPerson] = useState<User|undefined>();
  const [personHasAccount, setPersonHasAccount] = useState<boolean>(false);

  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [generatingNewPassword, setGeneratingNewPassword] = useState<boolean>(false);

  const history = useHistory();
  const updatePersonMode = !!updatePersonId;
  const [advancedAccountSettingsOpen, setAdvancedAccountSettingsOpen] = useState<boolean>(false);

  const setInitialsValues = (person: User): void => {
    setPerson(person);
    setInitials(person.initials || '');
    setFirstName(person.firstName || '');
    setLastName(person.lastName || '');
    setDateOfBirth(person.dateOfBirth);
  };

  useEffect(() => {
    if (!updatePersonId) {
      return;
    }
    setLoadingPerson(true);
    Api.getPerson(updatePersonId)
      .then(person => {
        setInitialsValues(person);
        return Api.getPersonCarewebId(person.id);
      })
      .then(carewebId => {
        setCarewebId(carewebId ?? undefined);
        return Api.getPersonAccount(updatePersonId);
      })
      .then(account => {
        setPersonHasAccount(!!account);
        setCreateAccount(!!account);
        setEmail(account ? account.email : '');
        setGeneratedPassword('');
        setLoadingPerson(false);
      })
      .catch(err => {
        console.error('Loading person failed:', err);
        NotificationHelper.toastError('Ophalen persoon mislukt door een onverwachte fout.');
      });
  }, [updatePersonId]);

  const formatBirthDate = (date: Date): string => {
    const year = date.getFullYear().toString();
    const monthNum = date.getMonth() + 1;
    const month = (monthNum < 10 ? `0${monthNum}` : monthNum.toString());
    const dayNum = date.getDate();
    const day = (dayNum < 10 ? `0${dayNum}` : dayNum.toString());
    return `${year}-${month}-${day}`;
  };

  const resolveInvitePersonError = (error: any): string => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    switch (error.key as string) {
      case 'EmailAlreadyTakenResponseError':
        return 'Emailadres is al in gebruik door een ander account';
      case 'PersonAlreadyHasAccountResponseError':
        return 'Persoon heeft al toegang (mogelijk via een ander emailadres)';
      case 'PersonNotFoundResponseError':
        return 'Persoon kan geen toegang krijgen doordat persoon niet bestaat';
      default:
        return 'Onverwachte fout';
    }
  };

  const updatePerson = async() => {
    if (!person || !updatePersonId) {
      return;
    }

    setSubmitting(true);

    const data: IUpdatePerson = {};
    if (initials !== person.initials) data.initials = initials;
    if (firstName !== person.firstName) data.firstName = firstName;
    if (lastName !== person.lastName) data.lastName = lastName;
    if (dateOfBirth && dateOfBirth.getTime() !== person.dateOfBirth?.getTime()) data.dateOfBirth = formatBirthDate(dateOfBirth);

    if (Object.keys(data).length !== 0) { // If any values have changed
      await Api.updatePerson(updatePersonId, data)
        .catch(err => {
          NotificationHelper.toastError('Gegevens aanpassen mislukt');
          setSubmitting(false);
          throw err;
        });
      NotificationHelper.toastSuccess('Gegevens aangepast', { silent: true });
      setPerson(new User({ ...person, ...data }));
    }

    if (!personHasAccount && createAccount && email) {
      await Api.invitePerson({
        personId: updatePersonId,
        email: email,
      })
        .catch(err => {
          const error = resolveInvitePersonError(err);
          setSubmitError(`Persoon toegang geven mislukt: ${error}`);
          setSubmitting(false);
          throw err;
        });
      NotificationHelper.toastSuccess('Persoon toegang gegeven', { silent: true });
      setPersonHasAccount(true);
    } else if (personHasAccount && !createAccount) {
      // Delete account from user
      await Api.removePersonAccount(updatePersonId)
        .catch(err => {
          setSubmitError('Account ontkoppelen mislukt');
          setSubmitting(false);
          throw err;
        });
      setPersonHasAccount(false);
      setEmail('');
      NotificationHelper.toastSuccess('Account ontkoppeld van persoon', { silent: true });
    }

    setSubmitting(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = validate({ showErrors: true });
    if (!isValid || !dateOfBirth) { // the !dateOfBirth check is unnecessary here, but it satisfies the typechecker.
      return;
    }

    if (updatePersonMode) {
      updatePerson()
        .catch(err => console.error('Failed to update person', err));
      return;
    }

    setSubmitting(true);
    let newPerson: User;
    Api.createPerson({
      carewebId,
      initials: initials.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: formatBirthDate(dateOfBirth)
    })
      .then(person => {
        newPerson = person;
      })
      .catch(err => { // Catch for creating person
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.key as string) {
          case 'PersonWithGivenInfoAlreadyExistsInCarewebError':
            setSubmitError('Een persoon met deze gegevens bestaat al in Careweb, bewerk dat persoon in plaats van een nieuw persoon aanmaken');
            break;
          case 'RequestingUserHasNoCarewebIdRequestError':
            setSubmitError('Deze gebruiker heeft geen carewebId en kan daardoor geen personen aanmaken');
            break;
          default:
            setSubmitError('Aanmaken gebruiker mislukt, probeer het later opnieuw');
            console.error('Failed to create new client', err);
            break;
        }
      })
      .then((): Promise<number|void> => {
        if (!newPerson || !createAccount) {
          return Promise.resolve(); // Act like everything is fine even when the then before failed, the user has gotten the error via the previous catch
        }
        // Create account
        return Api.invitePerson({ personId: newPerson.id, email });
      })
      .then(() => {
        if (newPerson && addToDossier) {
          history.push(`${ERouterPath.addPersonToDossier}?add-to-dossier=${addToDossier}&add-person=${newPerson.id}`);
        } else if (newPerson) {
          history.push(ERouterPath.listPersons);
        }
      })
      .catch(err => { // Catch for creating account for person
        const error = resolveInvitePersonError(err);
        setSubmitError(`Persoon succesvol toegevoegd, maar toegang geven mislukt: ${error}`);
      })
      .finally(() => setSubmitting(false));
  };

  const handleDatePickerChange = (date: Date | [Date, Date] | null): void => {
    setDateOfBirth(!date || Array.isArray(date) ? undefined : date);
  };

  const validate = (options: { showErrors?: boolean } = {}): boolean => {
    let valid = true;
    if (carewebId && (carewebId < 1 || !Number.isInteger(carewebId))) {
      if (options.showErrors) setCarewebIdError('Vul een geldig Careweb Id in');
      valid = false;
    }
    if (!initials) {
      if (options.showErrors) setInitialsError('Vul initialen in');
      valid = false;
    }
    if (!firstName) {
      if (options.showErrors) setFirstNameError('Vul roepnaam in.');
      valid = false;
    }
    if (!lastName) {
      if (options.showErrors) setLastNameError('Vul achternaam in.');
      valid = false;
    }
    if (!dateOfBirth) {
      if (options.showErrors) setDateOfBirthError('Vul geboortedatum in.');
      valid = false;
    } else {
      const dateOfBirthObject = new Date(dateOfBirth);
      if (dateOfBirthObject.getTime() > new Date().getTime()) {
        if (options.showErrors) setDateOfBirthError('Geboortedatum kan niet in de toekomst liggen.');
        valid = false;
      }
    }
    if (createAccount && !email) {
      if (options.showErrors) setEmailError('Vul een e-mailadres in.');
      valid = false;
    } else if (createAccount && !email.match(emailRegex)) {
      if (options.showErrors) setEmailError('E-mailadres is niet een geldig e-mailadres');
      valid = false;
    }

    return valid;
  };

  const generateNewPassword = (): void => {
    if (!personHasAccount || !email) {
      console.error('Cannot generate when person has no account or email is missing');
      return;
    }

    setGeneratingNewPassword(true);
    Api.resetAccountPassword({ email })
      .then(({ temporaryPassword }) => setGeneratedPassword(temporaryPassword))
      .catch(err => {
        console.error('Failed to reset password', err);
        NotificationHelper.toastError('Wachtwoord resetten mislukt', { silent: true });
      })
      .finally(() => setGeneratingNewPassword(false));
  };

  if (loadingPerson) {
    return <Spinner animation="border" variant="primary" />;
  }

  const listWithPersonsUrl = addToDossier ? `${ERouterPath.listPersons}?add-to-dossier=${addToDossier}` : ERouterPath.listPersons;

  return (
    <div className="add-person-input">
      <Form className="add-person-input__form" onSubmit={handleSubmit}>
        <div className="add-person-input__section">
          <div className="add-person__title-container">
            <h2>{ updatePersonMode ? 'Persoon bewerken' : 'Nieuw persoon' }</h2>
            {
              !updatePersonMode &&
              <Button className="add-person__list-persons" variant="link" onClick={() => history.push(listWithPersonsUrl)}>
                Lijst met personen
              </Button>
            }
          </div>
          <Form.Group className="form__item add-person-input__careweb-id">
            <Form.Label>Careweb ID</Form.Label>
            <Form.Control
              name={'INPUT_CAREWEB_ID'}
              type="number"
              autoComplete="off"
              onChange={e => setCarewebId(+e.target.value.trim())}
              value={`${carewebId || ''}`}
              isInvalid={!!carewebIdError}
              placeholder="Optioneel"
              disabled={updatePersonMode}
            />
            {
              !updatePersonMode &&
              <Form.Text className="text-muted">
                Wanneer hier geen Careweb ID wordt ingevoerd wordt er
                een nieuw persoon met de onderstaande gegevens aangemaakt in Careweb.
                Wanneer deze persoon al wel bestaat in Careweb dient het Careweb ID
                ingevuld te worden.
              </Form.Text>
            }
            { carewebIdError && <Alert className="add-person-input__danger" variant="danger">{carewebIdError}</Alert> }
          </Form.Group>

          <Form.Row>
            <Form.Group as={Col} className="form__item add-person-input__initials">
              <Form.Label>Initialen</Form.Label>
              <Form.Control
                name={'INPUT_INITIALS'}
                type="text"
                autoComplete="off"
                onChange={e => setInitials(e.target.value.toUpperCase().trim().replaceAll(/[^A-Z.]/g, ''))}
                value={initials}
                isInvalid={!!initialsError}
              />
              { initialsError && <Alert className="add-person-input__danger" variant="danger">{initialsError}</Alert> }
            </Form.Group>

            <Form.Group as={Col} className="form__item add-person-input__name">
              <Form.Label>Roepnaam</Form.Label>
              <Form.Control
                name={'INPUT_FIRST_NAME'}
                type="text"
                autoComplete="off"
                onChange={e => setFirstName(e.target.value.trimStart())}
                value={firstName}
                isInvalid={!!firstNameError}
              />
              { firstNameError && <Alert className="add-person-input__danger" variant="danger">{firstNameError}</Alert> }
            </Form.Group>
          </Form.Row>

          <Form.Row>
            <Form.Group as={Col} className="form__item">
              <Form.Label>Achternaam</Form.Label>
              <Form.Control
                name={'INPUT_LASTNAME'}
                type="text"
                autoComplete="off"
                onChange={e => setLastName(e.target.value.trimStart())}
                value={lastName}
                isInvalid={!!lastNameError}
              />
              { lastNameError && <Alert className="add-person-input__danger" variant="danger">{lastNameError}</Alert> }
            </Form.Group>

            <Form.Group as={Col} className="form__item add-person-input__birthdate">
              <Form.Label>Geboortedatum</Form.Label>
              <DatePicker
                selected={dateOfBirth}
                onChange={handleDatePickerChange}
                customInput={<Form.Control />}
                showYearDropdown
                scrollableYearDropdown
                maxDate={new Date()}
                locale="nl"
                dateFormat="dd-MM-yyyy"
                yearDropdownItemNumber={100}
              />
              { dateOfBirthError && <Alert className="add-person-input__danger" variant="danger">{dateOfBirthError}</Alert> }
            </Form.Group>
          </Form.Row>

          <Form.Group className="add-person__new-section">
            <h2>Deze persoon nu toegang tot MijnTOPP geven?</h2>
            <Form.Check
              type="switch"
              id="has-access"
              label={createAccount ? 'Toegang tot MijnTOPP' : 'Geen toegang tot MijnTOPP'}
              name={'INPUT_PERSON_EXISTS'}
              onChange={e => setCreateAccount(e.target.checked)}
              checked={createAccount}
              className="add-person-input__switch"
            />
          </Form.Group>

          {
            createAccount &&
            <Form.Group className="form__item">
              <Form.Label>E-mailadres</Form.Label>
              <Form.Control
                name={'INPUT_EMAIL'}
                type="text"
                autoComplete="off"
                onChange={e => setEmail(e.target.value)}
                value={email}
                isInvalid={!!emailError}
                disabled={personHasAccount}
              />
              { emailError && <Alert className="add-person-input__danger" variant="danger">{emailError}</Alert> }
            </Form.Group>
          }

          {
            personHasAccount &&
            <Form.Group className="form__item">
              <Form.Label>Geavanceerde opties</Form.Label>
              <Accordion className="add-person-input__advanced-options">
                <Accordion.Toggle
                  eventKey="0"
                  tab-index="0"
                  className={classNames('add-person-input__advanced-options-toggle', { 'add-person-input__advanced-options-toggle--active': advancedAccountSettingsOpen })}
                  onClick={() => setAdvancedAccountSettingsOpen(!advancedAccountSettingsOpen)}>
                  <Icon icon="dropdown" /> Wachtwoord resetten
                </Accordion.Toggle>
                <Accordion.Collapse eventKey="0">
                  <div className="add-person-input__advanced-options-body">
                    <p>Een tijdelijke wachtwoord vervangt het huidige wachtwoord. Bij het aanmelden met dit wachtwoord wordt het aanmaken van een nieuw wachtwoord verplicht. Dit tijdelijke wachtwoord is 14 dagen geldig.</p>

                    <p>Het nieuwe tijdelijk wachtwoord wordt enkel hier weergegeven en <strong>niet</strong> verzonden naar de persoon.</p>
                    <Button type="button" onClick={generateNewPassword}>Nieuw tijdelijk wachtwoord aanmaken</Button>
                    <p className="add-person-input__advanced-options-password">
                      { generatingNewPassword && <Spinner variant="primary" animation="border" /> }
                      { (generatedPassword && !generatingNewPassword) && <><strong>Tijdelijk wachtwoord:</strong> <code>{generatedPassword}</code></> }
                    </p>
                  </div>
                </Accordion.Collapse>
              </Accordion>
            </Form.Group>
          }
        </div>

        { submitError && <Alert className="add-person-input__danger" variant="danger">{submitError}</Alert> }
        <Button type="submit" disabled={submitting}>
          { submitting && <Spinner animation="border" variant="light" /> }
          { updatePersonMode ? 'Aanpassen' : 'Aanmaken' }
        </Button>
      </Form>

      { updatePersonId && <>
        <h2>Dossiers met toegang</h2>
        <PersonDossiers personId={updatePersonId} />
      </> }
    </div>
  );
};
